/**
 * Post Scheduler — IdeaWorks Platform
 * Multi-channel post scheduling: LinkedIn, Twitter/X, Instagram, TikTok, Meta, YouTube
 * Visual timeline + new post modal + status tracking + manual fire
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Clock, Plus, Zap, RefreshCw, Trash2, CheckCircle, AlertCircle,
  Edit2, Send, Calendar, ChevronLeft, ChevronRight, X, Eye, Copy,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'LinkedIn',        color: '#0A66C2', abbr: 'LI' },
  { id: 'Twitter/X',       color: '#1DA1F2', abbr: 'X' },
  { id: 'Instagram',       color: '#E1306C', abbr: 'IG' },
  { id: 'TikTok',          color: '#69C9D0', abbr: 'TT' },
  { id: 'Meta (Facebook)', color: '#1877F2', abbr: 'FB' },
  { id: 'YouTube',         color: '#FF0000', abbr: 'YT' },
];

const POST_TYPES = {
  LinkedIn:        ['post', 'article_link'],
  'Twitter/X':     ['post', 'thread'],
  Instagram:       ['post', 'reel', 'story'],
  TikTok:          ['short', 'post'],
  'Meta (Facebook)': ['post', 'reel'],
  YouTube:         ['short', 'post'],
};

const STATUS_CONFIG = {
  scheduled: { color: '#60a5fa', label: 'Scheduled',  bg: 'rgba(96,165,250,0.12)' },
  posting:   { color: '#f97316', label: 'Posting...',  bg: 'rgba(249,115,22,0.12)', spin: true },
  posted:    { color: '#3dffa0', label: 'Posted',     bg: 'rgba(61,255,160,0.12)' },
  ready:     { color: '#f0c27f', label: 'Ready',      bg: 'rgba(240,194,127,0.12)' },
  failed:    { color: '#f87171', label: 'Failed',     bg: 'rgba(248,113,113,0.12)' },
  cancelled: { color: '#6b7280', label: 'Cancelled',  bg: 'rgba(107,114,128,0.08)' },
};

const platMeta = (name) => PLATFORMS.find(p => p.id === name) || { color: '#888', abbr: '?' };

// ── Helper: group posts by date ───────────────────────────────────────────────
function groupByDate(posts) {
  const groups = {};
  posts.forEach(p => {
    const d = p.scheduled_at ? p.scheduled_at.slice(0, 10) : 'unknown';
    if (!groups[d]) groups[d] = [];
    groups[d].push(p);
  });
  return groups;
}

// ── Empty form state ──────────────────────────────────────────────────────────
const EMPTY_FORM = {
  title: '', content: '', platform: 'LinkedIn', post_type: 'post',
  scheduled_at: '', article_url: '', hashtags: '', notes: '',
};

// ── New Post Modal ────────────────────────────────────────────────────────────
function NewPostModal({ prefill, onClose, onCreated }) {
  const [form, setForm] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    return { ...EMPTY_FORM, ...(prefill || {}), scheduled_at: prefill?.scheduled_at || local };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!form.content.trim() || !form.scheduled_at) { setError('Content and scheduled time are required'); return; }
    setSaving(true);
    setError('');
    try {
      const hashtags = form.hashtags.trim() ? form.hashtags.split(/[\s,]+/).filter(Boolean) : [];
      const { data } = await axios.post(`${API}/api/scheduler/create`, {
        ...form, hashtags,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
      });
      onCreated(data.post);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to schedule post');
    }
    setSaving(false);
  };

  const charLimit = { LinkedIn: 3000, 'Twitter/X': 280, Instagram: 2200, TikTok: 2200, 'Meta (Facebook)': 63206, YouTube: 5000 }[form.platform] || 3000;
  const charsLeft = charLimit - form.content.length;
  const platInfo = platMeta(form.platform);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: 'var(--llmc-bg)', border: '1px solid var(--llmc-border)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black"
              style={{ background: `${platInfo.color}20`, color: platInfo.color, border: `1px solid ${platInfo.color}30` }}>
              {platInfo.abbr}
            </div>
            <div>
              <div className="text-sm font-black text-white">Schedule Post</div>
              <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>Will auto-publish at the scheduled time</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-all" style={{ color: 'var(--llmc-text-muted)' }}>
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Platform</label>
              <select value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value, post_type: (POST_TYPES[e.target.value] || ['post'])[0] }))}
                data-testid="scheduler-platform-select" className="llmc-input w-full px-3 py-2 text-sm">
                {PLATFORMS.map(p => <option key={p.id}>{p.id}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Post Type</label>
              <select value={form.post_type} onChange={e => setForm(f => ({ ...f, post_type: e.target.value }))}
                className="llmc-input w-full px-3 py-2 text-sm">
                {(POST_TYPES[form.platform] || ['post']).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Title (optional)</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="llmc-input w-full px-3 py-2 text-sm" placeholder="Post title or subject..." />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Content</label>
              <span className={`text-[10px] ${charsLeft < 0 ? 'text-red-400' : charsLeft < 50 ? 'text-orange-400' : ''}`} style={{ color: charsLeft >= 50 ? 'var(--llmc-text-muted)' : undefined }}>
                {charsLeft < 0 ? `${-charsLeft} over limit` : `${charsLeft} chars left`}
              </span>
            </div>
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              data-testid="scheduler-content-input"
              className="llmc-input w-full px-3 py-2.5 text-sm resize-none" rows={5}
              placeholder="Write your post content here..." />
          </div>

          {form.post_type === 'article_link' && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Article URL</label>
              <input value={form.article_url} onChange={e => setForm(f => ({ ...f, article_url: e.target.value }))}
                className="llmc-input w-full px-3 py-2 text-sm" placeholder="https://..." />
            </div>
          )}

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Hashtags (space or comma separated)</label>
            <input value={form.hashtags} onChange={e => setForm(f => ({ ...f, hashtags: e.target.value }))}
              className="llmc-input w-full px-3 py-2 text-sm" placeholder="#AI #ContentMarketing #IdeaWorks" />
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Schedule Date & Time (local)</label>
            <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
              data-testid="scheduler-datetime-input"
              className="llmc-input w-full px-3 py-2 text-sm" />
          </div>

          {error && <div className="text-xs text-red-400 px-3 py-2 rounded-lg" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>{error}</div>}

          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving || !form.content.trim() || !form.scheduled_at}
              data-testid="scheduler-submit-btn"
              className="flex-2 flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black transition-all hover:scale-105 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #ff6eb4, #60a5fa)', color: '#fff' }}>
              {saving ? <><RefreshCw size={12} className="animate-spin" /> Scheduling...</> : <><Clock size={12} /> Schedule Post</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Post Card ─────────────────────────────────────────────────────────────────
function PostCard({ post, onFire, onCancel, onCopy }) {
  const [expanded, setExpanded] = useState(false);
  const pm = platMeta(post.platform);
  const sc = STATUS_CONFIG[post.status] || STATUS_CONFIG.scheduled;
  const dt = post.scheduled_at ? new Date(post.scheduled_at) : null;

  return (
    <div className="rounded-2xl overflow-hidden transition-all" style={{ background: 'var(--llmc-surface)', border: `1px solid ${pm.color}25` }}>
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black mt-0.5"
              style={{ background: `${pm.color}20`, color: pm.color, border: `1px solid ${pm.color}30` }}>
              {pm.abbr}
            </div>
            <div className="min-w-0">
              {post.title && <div className="text-xs font-bold text-white truncate">{post.title}</div>}
              <div className="text-[11px] leading-snug mt-0.5 line-clamp-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {post.content}
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
              style={{ background: sc.bg, color: sc.color }}>
              {sc.label}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-2.5">
          <div className="flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
            <Clock size={9} />
            {dt ? dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setExpanded(e => !e)} className="p-1 rounded-lg hover:bg-white/5 transition-all" style={{ color: 'var(--llmc-text-muted)' }}>
              <Eye size={11} />
            </button>
            <button onClick={() => onCopy(post)} className="p-1 rounded-lg hover:bg-white/5 transition-all" style={{ color: 'var(--llmc-text-muted)' }}>
              <Copy size={11} />
            </button>
            {post.status === 'scheduled' && (
              <button onClick={() => onFire(post.id)} className="p-1 rounded-lg hover:opacity-80 transition-all" style={{ color: '#3dffa0' }} title="Post Now">
                <Send size={11} />
              </button>
            )}
            {['scheduled', 'failed', 'ready'].includes(post.status) && (
              <button onClick={() => onCancel(post.id)} className="p-1 rounded-lg hover:opacity-80 transition-all" style={{ color: '#f87171' }} title="Cancel">
                <Trash2 size={11} />
              </button>
            )}
          </div>
        </div>

        {post.post_url && (
          <a href={post.post_url} target="_blank" rel="noreferrer"
            className="block mt-1.5 text-[10px] underline truncate" style={{ color: pm.color }}>
            View on {post.platform} →
          </a>
        )}
        {post.error && (
          <div className="mt-1.5 text-[10px] px-2 py-1 rounded-lg" style={{ background: 'rgba(248,113,113,0.08)', color: '#f87171' }}>
            {post.error}
          </div>
        )}

        {expanded && (
          <div className="mt-2.5 text-xs leading-relaxed p-2.5 rounded-xl whitespace-pre-wrap"
            style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.7)', maxHeight: 200, overflowY: 'auto' }}>
            {post.content}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function SchedulerBoard() {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [firing, setFiring] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list | calendar

  const loadPosts = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPlatform !== 'all') params.platform = filterPlatform;
      const { data } = await axios.get(`${API}/api/scheduler/list`, { params });
      setPosts(data.posts || []);
      setStats(data.by_status || {});
    } catch { }
    setLoading(false);
  }, [filterStatus, filterPlatform]);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => {
    const interval = setInterval(loadPosts, 30000);
    return () => clearInterval(interval);
  }, [loadPosts]);

  const handleFire = async (postId) => {
    setFiring(f => ({ ...f, [postId]: true }));
    try {
      await axios.post(`${API}/api/scheduler/${postId}/fire`);
      await loadPosts();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to post');
    }
    setFiring(f => ({ ...f, [postId]: false }));
  };

  const handleCancel = async (postId) => {
    if (!window.confirm('Cancel this scheduled post?')) return;
    try {
      await axios.delete(`${API}/api/scheduler/${postId}`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: 'cancelled' } : p));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleCopy = (post) => {
    navigator.clipboard.writeText([post.title, post.content].filter(Boolean).join('\n\n'));
    setCopiedId(post.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleCreated = (post) => {
    setPosts(prev => [post, ...prev].sort((a, b) => (a.scheduled_at_ts || 0) - (b.scheduled_at_ts || 0)));
    setShowModal(false);
  };

  const grouped = groupByDate(posts);
  const dateKeys = Object.keys(grouped).sort();
  const today = new Date().toISOString().slice(0, 10);

  const STAT_CHIPS = [
    { id: 'all', label: 'All', count: posts.length, color: '#60a5fa' },
    { id: 'scheduled', label: 'Scheduled', count: stats.scheduled || 0, color: '#60a5fa' },
    { id: 'posted', label: 'Posted', count: stats.posted || 0, color: '#3dffa0' },
    { id: 'ready', label: 'Ready', count: stats.ready || 0, color: '#f0c27f' },
    { id: 'failed', label: 'Failed', count: stats.failed || 0, color: '#f87171' },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-5 pb-3 flex-shrink-0">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Clock size={18} style={{ color: '#ff6eb4' }} />
              <h1 className="text-lg font-black text-white">Post Scheduler</h1>
            </div>
            <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              Schedule content across LinkedIn, Twitter/X, Instagram, TikTok, Meta, and YouTube
            </p>
          </div>
          <button onClick={() => { setPrefill(null); setShowModal(true); }}
            data-testid="new-post-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #ff6eb4, #60a5fa)', color: '#fff' }}>
            <Plus size={14} /> New Post
          </button>
        </div>

        {/* Stats + Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {STAT_CHIPS.map(s => (
            <button key={s.id} onClick={() => setFilterStatus(s.id)}
              className="flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-semibold transition-all"
              style={{
                background: filterStatus === s.id ? `${s.color}20` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${filterStatus === s.id ? `${s.color}50` : 'rgba(255,255,255,0.08)'}`,
                color: filterStatus === s.id ? s.color : 'var(--llmc-text-muted)',
              }}>
              {s.label} {s.count > 0 && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black" style={{ background: `${s.color}25` }}>{s.count}</span>}
            </button>
          ))}
          <div className="ml-auto flex gap-1.5">
            {['all', ...PLATFORMS.map(p => p.id)].slice(0, 7).map(pid => {
              const pm = PLATFORMS.find(p => p.id === pid);
              return (
                <button key={pid} onClick={() => setFilterPlatform(pid)}
                  className="px-2.5 py-1 rounded-xl text-[10px] font-bold transition-all"
                  style={{
                    background: filterPlatform === pid ? `${pm?.color || '#60a5fa'}20` : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${filterPlatform === pid ? `${pm?.color || '#60a5fa'}50` : 'rgba(255,255,255,0.06)'}`,
                    color: filterPlatform === pid ? (pm?.color || '#60a5fa') : 'var(--llmc-text-muted)',
                  }}>
                  {pid === 'all' ? 'All' : (pm?.abbr || pid)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={20} className="animate-spin" style={{ color: '#ff6eb4' }} />
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-4 text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,110,180,0.08)', border: '1px solid rgba(255,110,180,0.15)' }}>
              <Clock size={36} style={{ color: 'rgba(255,110,180,0.4)' }} />
            </div>
            <div className="text-base font-bold text-white">No scheduled posts yet</div>
            <div className="text-xs max-w-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              Schedule posts to LinkedIn, Twitter/X, Instagram, TikTok, Meta, and YouTube — all from one place.
            </div>
            <button onClick={() => { setPrefill(null); setShowModal(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #ff6eb4, #60a5fa)', color: '#fff' }}>
              <Plus size={14} /> Schedule Your First Post
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {dateKeys.map(date => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-xs font-black uppercase tracking-wider" style={{ color: date === today ? '#ff6eb4' : 'var(--llmc-text-muted)' }}>
                    {date === today ? '📅 Today' : new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div className="flex-1 h-px" style={{ background: 'var(--llmc-border)' }} />
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
                    {grouped[date].length} post{grouped[date].length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {grouped[date].sort((a, b) => (a.scheduled_at_ts || 0) - (b.scheduled_at_ts || 0)).map(post => (
                    <PostCard
                      key={post.id}
                      post={{ ...post, _firing: firing[post.id] }}
                      onFire={handleFire}
                      onCancel={handleCancel}
                      onCopy={handleCopy}
                    />
                  ))}
                  {/* Add post to this date */}
                  <button
                    onClick={() => {
                      const d = new Date(date + 'T09:00:00');
                      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                      setPrefill({ scheduled_at: local });
                      setShowModal(true);
                    }}
                    className="rounded-2xl p-4 flex flex-col items-center justify-center gap-2 text-xs font-semibold transition-all hover:opacity-80"
                    style={{ border: '1px dashed rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.2)', minHeight: 80 }}>
                    <Plus size={16} />
                    Add to {date === today ? 'today' : 'this day'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Post Modal */}
      {showModal && (
        <NewPostModal
          prefill={prefill}
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
